import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Boxes, FolderGit2, Inbox, KeyRound, LayoutGrid, LogOut, type LucideIcon } from 'lucide-react';
import { api, isMock } from '../api/client';
import { q } from '../api/queries';
import { useLiveConnected, useLiveUpdates } from '../api/live';
import { requireSession } from '../lib/auth';
import { cn } from '@bounded/ui/cn';
import { Mark } from '@bounded/ui/auth-layout';
import { LanguageSelect } from '@bounded/ui/language';
import { useT } from '../i18n';
import type { Messages } from '../i18n';

export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => requireSession(context.queryClient, location.href),
  component: Shell,
});

const nav: { to: string; label: keyof Messages['nav']; icon: LucideIcon; exact?: boolean }[] = [
  { to: '/', label: 'overview', icon: LayoutGrid, exact: true },
  { to: '/inbox', label: 'inbox', icon: Inbox },
  { to: '/projects', label: 'projects', icon: FolderGit2 },
  { to: '/devices', label: 'devices', icon: Boxes },
  { to: '/models', label: 'models', icon: KeyRound },
  { to: '/activity', label: 'activity', icon: Activity },
];

function Shell() {
  const t = useT();
  useLiveUpdates(!isMock);
  const live = useLiveConnected();
  const overview = useQuery(q.overview());
  const me = useQuery(q.me());
  const pending = overview.data?.approvals.waiting ?? 0;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const signOut = useMutation({ mutationFn: () => api.signOut(), onSettled: async () => { queryClient.clear(); await navigate({ to: '/sign-in', search: { next: undefined } }); } });

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[13.5rem_1fr]">
      <aside className="sticky top-0 z-10 flex flex-col border-b border-rule bg-paper lg:h-dvh lg:border-r lg:border-b-0">
        <div className="flex h-12 items-center gap-2 px-4 lg:h-14">
          <Mark size={18} />
          <span className="text-lg font-semibold tracking-[-0.01em]">Bounded</span>
          {isMock && <span className="ml-auto rounded border border-dashed border-rule-strong px-1.5 text-xs text-graphite" title={t.common.fixturesTitle}>{t.common.fixtures}</span>}
          {/* Below lg the account footer is hidden; keep language and sign-out reachable in the top bar. */}
          <div className={cn('flex items-center gap-3 lg:hidden', !isMock && 'ml-auto')}>
            <LanguageSelect compact />
            <button type="button" onClick={() => signOut.mutate()} aria-label={t.common.signOut} title={t.common.signOut} className="text-graphite hover:text-ink"><LogOut size={16} aria-hidden /></button>
          </div>
        </div>
        <nav aria-label={t.nav.main} className="flex gap-0.5 overflow-x-auto px-2 pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
          {nav.map(({ to, label, icon: Icon, exact }) => (
            <Link key={to} to={to} activeOptions={{ exact }}
              className="flex h-8 shrink-0 items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 text-graphite hover:bg-sunken hover:text-ink data-[status=active]:bg-surface data-[status=active]:font-medium data-[status=active]:text-ink data-[status=active]:shadow-[inset_0_0_0_1px_var(--rule)]">
              <Icon size={16} strokeWidth={1.75} aria-hidden />
              {t.nav[label] as string}
              {to === '/inbox' && pending > 0 && <span className="ml-auto min-w-5 rounded-full bg-ask px-1.5 text-center text-xs font-semibold text-surface tabular-nums" aria-label={t.nav.waiting(pending)}>{pending}</span>}
            </Link>
          ))}
        </nav>
        {/* Account: who is signed in and how the console hears about changes, then one row of controls. */}
        <div className="mt-auto hidden border-t border-rule lg:block">
          <div className="grid min-w-0 gap-0.5 px-3 py-3">
            <p className="truncate text-sm font-medium">{me.data?.user.name}</p>
            <p className="truncate text-sm text-graphite" title={me.data?.user.email}>{me.data?.user.email}</p>
            {!isMock && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-graphite" title={live ? t.live.onTitle : t.live.offTitle}>
                <span aria-hidden className={cn('size-1.5 rounded-full', live ? 'bg-ink' : 'border border-dashed border-graphite')} />{live ? t.live.on : t.live.off}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-rule px-3 py-2">
            <LanguageSelect compact />
            <button type="button" onClick={() => signOut.mutate()} className="-mr-1.5 inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-1.5 text-sm text-graphite hover:bg-surface hover:text-ink"><LogOut size={14} aria-hidden />{t.common.signOut}</button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 px-4 py-6 sm:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  );
}
